/**
 * Analysis chat page (/:orgSlug/analysis and /:orgSlug/analysis/:id).
 *
 * Layout:
 *   ┌─────────────┬─────────────────────────────────┐
 *   │ AnalysisList│ AnalysisHeader                  │
 *   │ (sidebar)   ├─────────────────────────────────┤
 *   │             │ MessageList                     │
 *   │             │                                 │
 *   │             │ Composer                        │
 *   └─────────────┴─────────────────────────────────┘
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Send, Eye, EyeOff, Loader2, Square, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'

import {
  AnalysisDetail,
  AnalysisMessage,
  AnalysisSummary,
  Segment,
  StreamEvent,
  createAnalysis,
  deleteAnalysis,
  getAnalysis,
  listAnalyses,
  patchAnalysis,
  streamTurn,
  submitMessage,
} from '@/services/analysis'
import { MessageRenderer } from './analysis-widgets'

export function AnalysisPage() {
  const { orgSlug, id } = useParams<{ orgSlug: string; id?: string }>()
  const navigate = useNavigate()

  const [list, setList] = useState<AnalysisSummary[]>([])
  const [active, setActive] = useState<AnalysisDetail | null>(null)
  const [streamingMessage, setStreamingMessage] = useState<AnalysisMessage | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<AnalysisSummary | null>(null)
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const closeStreamRef = useRef<(() => void) | null>(null)

  const refreshList = useCallback(async () => {
    try {
      const items = await listAnalyses()
      setList(items)
    } catch (e) {
      console.error('listAnalyses failed', e)
    }
  }, [])

  // Initial list load.
  useEffect(() => {
    refreshList()
  }, [refreshList])

  // Load the active analysis whenever the URL :id changes.
  useEffect(() => {
    if (!id) {
      setActive(null)
      return
    }
    let cancelled = false
    getAnalysis(Number(id))
      .then((d) => {
        if (!cancelled) setActive(d)
      })
      .catch((e) => {
        console.error('getAnalysis failed', e)
        toast.error('Failed to load analysis')
      })
    return () => {
      cancelled = true
    }
  }, [id])

  // Tear down any streaming on unmount.
  useEffect(() => {
    return () => closeStreamRef.current?.()
  }, [])

  const startRename = useCallback((a: AnalysisSummary) => {
    setRenamingId(a.id)
    setRenameValue(a.name)
  }, [])

  const commitRename = useCallback(async () => {
    if (renamingId === null) return
    const next = renameValue.trim()
    const target = list.find((x) => x.id === renamingId)
    setRenamingId(null)
    if (!target || !next || next === target.name) return
    try {
      const updated = await patchAnalysis(renamingId, { name: next })
      setList((prev) => prev.map((x) => (x.id === renamingId ? { ...x, name: updated.name } : x)))
      setActive((prev) => (prev && prev.id === renamingId ? { ...prev, name: updated.name } : prev))
    } catch (e) {
      console.error('patchAnalysis failed', e)
      toast.error('Failed to rename analysis')
    }
  }, [list, renameValue, renamingId])

  const handleNew = useCallback(async () => {
    try {
      const created = await createAnalysis()
      await refreshList()
      navigate(`/${orgSlug}/analysis/${created.id}`)
    } catch (e) {
      console.error('createAnalysis failed', e)
      toast.error('Failed to create analysis')
    }
  }, [navigate, orgSlug, refreshList])

  const handleSend = useCallback(
    async (text: string) => {
      if (!id || !text.trim()) return
      const analysisId = Number(id)

      // Append user message optimistically.
      setActive((prev) =>
        prev
          ? {
              ...prev,
              messages: [
                ...prev.messages,
                {
                  id: -Date.now(),
                  role: 'user',
                  content: [{ type: 'text', text }],
                  created_at: new Date().toISOString(),
                },
              ],
            }
          : prev
      )
      // Initialize an in-progress assistant message.
      const draft: AnalysisMessage = {
        id: -Date.now() - 1,
        role: 'assistant',
        content: [],
        created_at: new Date().toISOString(),
      }
      setStreamingMessage(draft)
      setIsStreaming(true)

      try {
        await submitMessage(analysisId, text)
      } catch (e) {
        console.error('submitMessage failed', e)
        toast.error('Failed to submit message')
        setIsStreaming(false)
        setStreamingMessage(null)
        return
      }

      // Build live segment list as events arrive.
      const segments: Segment[] = [{ type: 'markdown', text: '' }]
      let lastMarkdownIdx = 0

      const onEvent = (e: StreamEvent) => {
        if (e.type === 'token') {
          const last = segments[lastMarkdownIdx]
          if (last && last.type === 'markdown') {
            last.text += e.delta
          } else {
            segments.push({ type: 'markdown', text: e.delta })
            lastMarkdownIdx = segments.length - 1
          }
          setStreamingMessage({ ...draft, content: [...segments] })
        } else if (e.type === 'tool_call') {
          segments.push({
            type: 'tool_call',
            id: e.id,
            name: e.name,
            args: e.args,
          })
          // After a tool_call, subsequent text should land in a NEW markdown segment.
          segments.push({ type: 'markdown', text: '' })
          lastMarkdownIdx = segments.length - 1
          setStreamingMessage({ ...draft, content: [...segments] })
        } else if (e.type === 'tool_result') {
          // We only know the row count + columns from the stream; the full
          // result is fetched by re-loading the analysis after `done`.
          // Push a placeholder segment that will be replaced on reload.
          segments.push({
            type: 'tool_result',
            id: e.id,
            rows: [],
            columns: e.columns,
            truncated: e.truncated,
            error: e.error,
          })
          segments.push({ type: 'markdown', text: '' })
          lastMarkdownIdx = segments.length - 1
          setStreamingMessage({ ...draft, content: [...segments] })
        } else if (e.type === 'error') {
          toast.error(e.message)
          setIsStreaming(false)
        } else if (e.type === 'done') {
          setIsStreaming(false)
          // Reload the analysis to pick up persisted (and full-row) tool_result
          // segments. This is also what restores tool_result rows.
          getAnalysis(analysisId).then((d) => {
            setActive(d)
            setStreamingMessage(null)
          })
          refreshList()
        }
      }

      closeStreamRef.current?.()
      closeStreamRef.current = streamTurn(analysisId, onEvent)
    },
    [id, refreshList]
  )

  // ----- Sidebar groups -----------------------------------------------------
  const grouped = groupByDate(list)

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r flex flex-col">
        <div className="border-b p-3">
          <Button onClick={handleNew} className="w-full" size="sm">
            <Plus className="mr-1 h-4 w-4" /> New analysis
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-2">
          {grouped.map(({ label, items }) => (
            <div key={label} className="mb-2">
              <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                {label}
              </div>
              {items.map((a) => {
                const isActive = Number(id) === a.id
                const isRenaming = renamingId === a.id
                return (
                  <div
                    key={a.id}
                    className={`group relative flex items-center rounded text-sm transition-colors ${
                      isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                    }`}
                  >
                    {isRenaming ? (
                      <Input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                          if (e.key === 'Escape') {
                            setRenamingId(null)
                          }
                        }}
                        autoFocus
                        className="h-7 px-2 text-sm"
                      />
                    ) : (
                      <>
                        <button
                          onClick={() => navigate(`/${orgSlug}/analysis/${a.id}`)}
                          className="flex-1 truncate px-2 py-1.5 text-left"
                          title={a.name}
                        >
                          {a.shared && <Eye className="mr-1 inline h-3 w-3 align-text-bottom" />}
                          {a.name}
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={`mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground focus:opacity-100 data-[state=open]:opacity-100 ${
                                isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Chat options"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onSelect={() => startRename(a)}>
                              <Pencil className="mr-2 h-4 w-4" /> Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => setPendingDelete(a)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
          {!list.length && (
            <div className="px-2 py-4 text-xs text-muted-foreground">
              No analyses yet. Click "New analysis" to start.
            </div>
          )}
        </div>
      </aside>

      {/* Main pane */}
      <main className="flex flex-1 flex-col">
        {!active ? (
          <EmptyState onNew={handleNew} />
        ) : (
          <>
            <AnalysisHeader
              analysis={active}
              onPatch={async (patch) => {
                const updated = await patchAnalysis(active.id, patch)
                setActive((prev) => (prev ? { ...prev, ...updated } : prev))
                refreshList()
              }}
            />
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {active.messages.map((m) => (
                <MessageRenderer key={m.id} message={m} />
              ))}
              {streamingMessage && (
                <MessageRenderer message={streamingMessage} />
              )}
              {isStreaming && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> thinking…
                </div>
              )}
            </div>
            <Composer
              onSend={handleSend}
              onStop={() => {
                closeStreamRef.current?.()
                closeStreamRef.current = null
                setIsStreaming(false)
              }}
              disabled={isStreaming}
            />
          </>
        )}
      </main>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `"${pendingDelete.name}" will be archived. Existing reports saved from this analysis stay in place.`
                : 'It will be archived. Existing reports stay in place.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingDelete) return
                const target = pendingDelete
                setPendingDelete(null)
                try {
                  await deleteAnalysis(target.id)
                  toast.success('Analysis deleted')
                  refreshList()
                  if (active?.id === target.id) {
                    navigate(`/${orgSlug}/analysis`)
                  }
                } catch (err) {
                  console.error('deleteAnalysis failed', err)
                  toast.error('Failed to delete analysis')
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers / sub-components
// ---------------------------------------------------------------------------

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center">
      <h1 className="text-2xl font-semibold">Analysis</h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Ask questions about your data. The assistant has access to your metrics,
        dimensions, and a query tool — it can produce tables and charts inline.
      </p>
      <Button className="mt-4" onClick={onNew}>
        <Plus className="mr-1 h-4 w-4" /> New analysis
      </Button>
    </div>
  )
}

interface HeaderProps {
  analysis: AnalysisDetail
  onPatch: (p: { name?: string; shared?: boolean }) => Promise<void>
}

function AnalysisHeader({ analysis, onPatch }: HeaderProps) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(analysis.name)
  useEffect(() => setName(analysis.name), [analysis.name])

  return (
    <header className="flex items-center justify-between border-b px-6 py-3">
      <div className="flex-1">
        {editing ? (
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              setEditing(false)
              if (name !== analysis.name) onPatch({ name })
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') {
                setName(analysis.name)
                setEditing(false)
              }
            }}
            autoFocus
            className="h-8 w-96"
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-base font-medium hover:underline"
          >
            {analysis.name}
          </button>
        )}
        <div className="mt-0.5 text-xs text-muted-foreground">
          ${analysis.total_cost_usd.toFixed(4)} ·{' '}
          {analysis.total_input_tokens + analysis.total_output_tokens} tokens
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPatch({ shared: !analysis.shared })}
          title={analysis.shared ? 'Visible to org — click to make private' : 'Private — click to share with org'}
        >
          {analysis.shared ? (
            <Eye className="mr-1 h-4 w-4" />
          ) : (
            <EyeOff className="mr-1 h-4 w-4" />
          )}
          {analysis.shared ? 'Public' : 'Private'}
        </Button>
      </div>
    </header>
  )
}

interface ComposerProps {
  onSend: (text: string) => void
  onStop?: () => void
  disabled?: boolean
}

function Composer({ onSend, onStop, disabled }: ComposerProps) {
  const [text, setText] = useState('')
  const submit = () => {
    const trimmed = text.trim()
    if (!trimmed) return
    onSend(trimmed)
    setText('')
  }
  return (
    <div className="border-t bg-background p-4">
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          placeholder="Ask about your data… (Cmd-Enter to send)"
          rows={2}
          className="resize-none"
          disabled={disabled}
        />
        {disabled && onStop ? (
          <Button onClick={onStop} variant="destructive" title="Stop generating">
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={disabled || !text.trim()}>
            {disabled ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

interface DateGroup {
  label: string
  items: AnalysisSummary[]
}

function groupByDate(items: AnalysisSummary[]): DateGroup[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const sevenAgo = new Date(today)
  sevenAgo.setDate(today.getDate() - 7)

  const groups: Record<string, AnalysisSummary[]> = {
    Today: [],
    'Last 7 days': [],
    Older: [],
  }
  for (const a of items) {
    const t = new Date(a.last_message_at)
    if (t >= today) groups['Today'].push(a)
    else if (t >= sevenAgo) groups['Last 7 days'].push(a)
    else groups['Older'].push(a)
  }
  return Object.entries(groups)
    .filter(([, v]) => v.length)
    .map(([label, items]) => ({ label, items }))
}
